import { z } from 'zod';
import { GameOverrideEventSchema } from './variant';
import { SkinRecordSchema } from './skin';

export const EventStatusSchema = z.enum(['draft', 'open', 'started', 'ended']);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const EventStateSchema = z.object({
	globalChatMessagesRead: z.number().default(0),
	teamChatMessagesRead: z.record(z.string(), z.number()).default({}),
	startedAt: z.string().datetime().optional().nullable(),
	mediaArchived: z.enum(['not-archived', 'archived', 'no-media']).default('not-archived'),
	mediaLinkSent: z.string().datetime().nullish(),
	autoEnd: z.boolean().default(true),
	frozen: z.boolean().default(false),
});
export type EventState = z.infer<typeof EventStateSchema>;

const emailSchema = z.preprocess((v) => {
	if (v === '' || v == null) return undefined;
	return typeof v === 'string' ? v.trim() : v;
}, z.string().email().nullish());

export const EventSchema = z.object({
	name: z.string().default(''),
	code: z
		.string()
		.min(3)
		.transform((e) => e.toUpperCase())
		.default('')
		.nullable(),
	oldCode: z.string().nullish(),
	status: EventStatusSchema.default('draft'),
	gameId: z.string().uuid(),
	customerEmail: emailSchema,
	variantId: z.string().uuid().optional().nullable(),
	gameOverrides: GameOverrideEventSchema.optional().nullable(),
	state: EventStateSchema.default({}),
	archived: z.boolean().optional(),
});
export type Event = z.infer<typeof EventSchema>;

export const EventRecordSchema = EventSchema.extend({
	id: z.string().uuid(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});
export type EventRecord = z.infer<typeof EventRecordSchema>;

export const EventRecordUpdateSchema = EventSchema.extend({
	id: z.string().uuid(),
});
export type EventRecordUpdate = z.infer<typeof EventRecordUpdateSchema>;

export const EventViewSchema = z.object({
	id: z.string(),
	name: z.string(),
	code: z.string().nullable(),
	status: EventStatusSchema,
	gameName: z.string(),
	eventType: z.string(),
	date: z.string(),
	variantName: z.string(),
	lang: z.string(),
	gameId: z.string(),
	variantId: z.string().nullable(),
	skin: SkinRecordSchema.nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	archived: z.boolean(),
	gameOverrides: GameOverrideEventSchema.optional().nullable(),
});
export type EventView = z.infer<typeof EventViewSchema>;
